import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import AlertMessage from "../Common/AlertMessage";

import {
  createCompanyLicense,
  getCompanyLicenseById,
  updateCompanyLicense,
} from "../../services/companyLicenseService";
import { getCompanies } from "../../services/companyService";
import extractApiErrors from "../../utils/extractApiErrors";
import { toLocalDateTime, toApiDateTimeString } from "../../utils/formatters";
import { Card, Container } from "react-bootstrap";
import SelectEmpresa from "../Common/SelectEmpresa";
import DatePicker, { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
// import "react-datepicker/dist/react-datepicker.css";
registerLocale("es", es);

const CompanyLicenseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [companies, setCompanies] = useState([]);
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const validationSchema = Yup.object().shape({
    companyId: Yup.string().required("La empresa es obligatoria"),
    isUnlimited: Yup.boolean(),
    maxUsers: Yup.number().when("isUnlimited", {
      is: true,
      then: Yup.number().oneOf([0], "Debe ser 0 si es ilimitada"),
      otherwise: Yup.number()
        .min(1, "Debe ser mayor que 0 si no es ilimitada")
        .required("Este campo es obligatorio"),
    }),
    startDate: Yup.date().nullable(),
    endDate: Yup.date()
      .nullable()
      .when("startDate", (startDate, schema) => {
        return startDate
          ? schema.min(startDate, "La fecha fin debe ser posterior a inicio")
          : schema;
      }),
    notes: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      companyId: "",
      maxUsers: 0,
      isUnlimited: false,
      startDate: "",
      endDate: "",
      notes: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        const payload = {
          ...values,
          startDate: toApiDateTimeString(values.startDate),
          endDate: toApiDateTimeString(values.endDate),
        };
        const response = id
          ? await updateCompanyLicense({ ...payload, companyLicenseId: id })
          : await createCompanyLicense(payload);

        if (!response.success) throw { backend: response };

        setSuccessMessage(
          id
            ? "✅ Licencia actualizada correctamente."
            : "✅ Licencia creada correctamente."
        );
      } catch (error) {
        const parsedErrors = extractApiErrors(error);
        setErrors(parsedErrors);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    getCompanies(1, 100).then((res) => {
      if (res.success) {
        setCompanies(res.data.items.filter((c) => c.isActive));
      }
    });
  }, []);

  useEffect(() => {
    if (id) {
      getCompanyLicenseById(id)
        .then((res) => {
          const {
            companyId,
            maxUsers,
            isUnlimited,
            startDate,
            endDate,
            notes,
          } = res.data;
          formik.setValues({
            companyId,
            maxUsers,
            isUnlimited,
            startDate: startDate ? new Date(startDate) : "",
            endDate: endDate ? new Date(endDate) : "",
            notes: notes || "",
          });
        })
        .finally();
    }
  }, [id]);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        navigate("/dashboard/company-license");
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage, navigate]);

  return (
    <Container className="mt-4">
      <Card className="p-4 novian-form">
        <Card.Header>
          <Card.Title as="h3" className="text-dark">
            {id ? "Editar licencia" : "Crear Nueva licencia"}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <form onSubmit={formik.handleSubmit}>
            <div className="row mb-3">
              <div className="col-12">
                <label className="form-label">Empresa</label>
                <SelectEmpresa
                  options={companies}
                  value={formik.values.companyId}
                  onChange={(val) => formik.setFieldValue("companyId", val)}
                  isDisabled={!!id}
                />
                {formik.touched.companyId && formik.errors.companyId && (
                  <div className="text-danger small mt-1">
                    {formik.errors.companyId}
                  </div>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 d-flex flex-column justify-content-center">
                <label className="form-label d-block">¿Es ilimitada?</label>
                <div className="material-switch">
                  <input
                    id="isUnlimited"
                    name="isUnlimited"
                    type="checkbox"
                    checked={formik.values.isUnlimited}
                    onChange={formik.handleChange}
                  />
                  <label
                    htmlFor="isUnlimited"
                    className="label-primary"
                  ></label>
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Usuarios máximos</label>
                <input
                  type="number"
                  name="maxUsers"
                  className="form-control"
                  value={formik.values.maxUsers}
                  onChange={formik.handleChange}
                  min={0}
                />
                {formik.touched.maxUsers && formik.errors.maxUsers && (
                  <div className="text-danger small mt-1">
                    {formik.errors.maxUsers}
                  </div>
                )}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6 datepicker-wrapper">
                <label className="form-label">Fecha de inicio</label>
                <DatePicker
                  selected={formik.values.startDate}
                  onChange={(date) => formik.setFieldValue("startDate", date)}
                  locale="es"
                  showTimeSelect
                  dateFormat="Pp"
                  timeFormat="HH:mm"
                  className="form-control"
                  placeholderText="Selecciona fecha y hora"
                />
              </div>
              <div className="col-md-6 datepicker-wrapper">
                <label className="form-label">Fecha de fin</label>
                <DatePicker
                  selected={formik.values.endDate}
                  onChange={(date) => formik.setFieldValue("endDate", date)}
                  locale="es"
                  showTimeSelect
                  dateFormat="Pp"
                  timeFormat="HH:mm"
                  className="form-control"
                  placeholderText="Selecciona fecha y hora"
                  minDate={formik.values.startDate}
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <div className="text-danger small mt-1">
                    {formik.errors.endDate}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Notas</label>
              <textarea
                name="notes"
                className="form-control"
                rows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
              />
            </div>

            {/* mensajes */}
            {errors.length > 0 && (
              <AlertMessage
                type="error"
                message={
                  <ul className="mb-0">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                }
                onClose={() => setErrors([])}
              />
            )}

            {successMessage && (
              <AlertMessage
                type="success"
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            )}

            {/* botones */}
            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/dashboard/company-license")}
              >
                Volver
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting
                  ? "Guardando..."
                  : id
                  ? "Actualizar"
                  : "Crear"}
              </button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CompanyLicenseForm;
